<template>
  <section id="mainContainer">
    <userHeader></userHeader>
    <section class="mid-container">
      <section class="customer-detail-section">
        <div class="container">
          <h2>Case Study</h2>
          <div class="customer-detail-inner">
            <div class="customer-detail-sidebar">
              <div class="customer-logo">
                <img :src="getCdnImageAddress(clientData.companyLogo, `w_248,h_202/`)" :alt="clientData.title" />
              </div>
              <div class="short-details">
                <p v-html="truncatedDescription"></p>
              </div>
              <div class="related-case-study">
                <h4>Related Case Studies</h4>
                <ul>
                  <li v-for="relatedClient in relatedCaseStudies" :key="relatedClient.id">
                    <div v-if="shouldShowClientReadMore(relatedClient)">
                      <a href="#" @click.prevent="navigateToClient(relatedClient.slug)">
                        <img :src="getCdnImageAddress(relatedClient.companyLogo, `w_120,h_98/`)" :alt="relatedClient.title" />
                      </a>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
            <div class="customer-detail-content">
              <h1>{{ clientData.title }}</h1>
              <div class="c_d_des">
                <p v-html="clientData.description"></p>
              </div>
              <div class="case-study-slider">
                <div id="carouselExampleIndicators" class="carousel slide" data-bs-ride="carousel">
                  <div class="carousel-inner">
                    <div class="carousel-item active">
                      <img :src="getCdnImageAddress(clientData.companyImage, `w_719,h_429/`)" :alt="clientData.title" />
                    </div>
                  </div>
                </div>
              </div>
              <div class="case-study-tabs">
                <div class="case-study-tabs--top">
                  <ul class="nav" id="myTab" role="tablist">
                    <li>
                      <a class="active" id="tab-a" data-bs-toggle="tab" href="#taba" role="tab" aria-controls="taba" aria-selected="true">Challenge</a>
                    </li>
                    <li>
                      <a id="tab-b" data-bs-toggle="tab" href="#tabb" role="tab" aria-controls="tabb" aria-selected="false">Solutions</a>
                    </li>
                    <li>
                      <a id="tab-c" data-bs-toggle="tab" href="#tabc" role="tab" aria-controls="tabc" aria-selected="false">Result</a>
                    </li>
                  </ul>
                </div>
                <div class="case-study-tabs--btm tab-content" id="myTabContent">
                  <div class="tab-pane fade show active" id="taba" role="tabpanel" aria-labelledby="tab-a">
                    <p v-html="clientData.challenge"></p>
                  </div>
                  <div class="tab-pane fade" id="tabb" role="tabpanel" aria-labelledby="tab-b">
                    <p v-html="clientData.solution"></p>
                  </div>
                  <div class="tab-pane fade" id="tabc" role="tabpanel" aria-labelledby="tab-c">
                    <p v-html="clientData.result"></p>
                  </div>
                </div>
              </div>
            </div>
            <div class="clear"></div>
          </div>
        </div>
      </section>
    </section>
    <userFooter></userFooter>
  </section>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import userHeader from "@/components/userHeader.vue";
import axios from 'axios';
import { useRoute, useRouter } from 'vue-router';
import userFooter from "@/components/userFooter.vue";
import { environment } from '@/config/environment';
import { API_BASE_URL } from '@/config/api.config';
import { useHead } from '@vueuse/head';

interface Client {
  id: number;
  title: string;
  description: string | null;  // Allow null if description can be null
  challenge: string;
  solution: string;
  result: string;
  companyImage: string;
  companyLogo: string;
  slug: string;
  status: number;
}

export default defineComponent({
  name: "readMoreClient",
  components: {
    userHeader,
    userFooter
  },
  data() {
    return {
      clientSlug: '' as string,
      clientData: {
        title: '',
        challenge: '',
        description: '',
        result: '',
        solution: '',
        companyImage: '',
        companyLogo: '',
        slug: ''
      } as Client,
      Allclients: [] as Client[],
      currentClientIndex: 0
    };
  },
  computed: {
    truncatedDescription(): string {
      if (!this.clientData.description) return '';
      return this.clientData.description.length > 100
        ? this.clientData.description.substring(0, 100) + '...'
        : this.clientData.description;
    },
    metaDescription(): string {
      if (!this.clientData.description) return 'Read about our case study with ' + this.clientData.title + ' at LMD.';
      return this.clientData.description.length > 160
        ? this.clientData.description.substring(0, 157) + '...'
        : this.clientData.description;
    },
    relatedCaseStudies(): Client[] {
      if (!this.Allclients.length) return [];

      // Exclude the current client and filter by status
      const otherClients = this.Allclients.filter(
        client => client.slug !== this.clientSlug && client.status === 1
      );

      if (otherClients.length <= 2) {
        return otherClients;
      }

      // Get previous and next indices from all clients
      const totalClients = this.Allclients.length;
      let prevIndex = this.currentClientIndex - 1;
      let nextIndex = this.currentClientIndex + 1;

      // Handle wraparound
      if (prevIndex < 0) prevIndex = totalClients - 1;
      if (nextIndex >= totalClients) nextIndex = 0;

      // Ensure we don't include the current client
      const related = [];
      if (this.Allclients[prevIndex].slug !== this.clientSlug) {
        related.push(this.Allclients[prevIndex]);
      }
      if (this.Allclients[nextIndex].slug !== this.clientSlug) {
        related.push(this.Allclients[nextIndex]);
      }

      // If we need more to fill up to 2, pick random clients
      while (related.length < 2 && otherClients.length > 0) {
        const randomIndex = Math.floor(Math.random() * otherClients.length);
        related.push(otherClients.splice(randomIndex, 1)[0]);
      }

      return related.slice(0, 2);
    }
  },
  methods: {
    async fetchClientData() {
      try {
        const clientId = this.clientSlug;
        if (!clientId) {
          throw new Error('Client slug is missing');
        }
        const [clientResponse, clientsResponse] = await Promise.all([
          axios.get(`${API_BASE_URL}/client/${clientId}/`),
          axios.get(`${API_BASE_URL}/client/?status=1`)
        ]);
        this.clientData = clientResponse.data;
        useHead({
          // title: this.clientData.title,
          meta: [
          {
              name: 'description',
              content: this.metaDescription
            },
            {
              name: 'keywords',
              content: `${this.clientData.title}, case study, LMD, client success`
            },
            {
              property: 'og:title',
              content: `${this.clientData.title} - LMD Case Study`
            },
            {
              property: 'og:description',
              content: this.metaDescription
            }
          ]
        });
        this.Allclients = clientsResponse.data.filter((client: Client) => client.status === 1);
        this.currentClientIndex = this.Allclients.findIndex(
          (client: Client) => client.slug === this.clientSlug
        );
        // Scroll to top after data is loaded
        this.onTop();
      } catch (err) {
        console.error('Error fetching client data:', err);
      }
    },
    getCdnImageAddress(src: string, size: string): string {
      if (!src) return '';
      let cdnUrl: string = environment.imageCdn.getHost;
      cdnUrl = cdnUrl.concat(size);
      return cdnUrl + encodeURIComponent(src);
    },
    shouldShowClientReadMore(client: Client): boolean {
      return !!client.description && client.description.length > 180;
    },
    navigateToClient(clientSlug: string) {
      if (!clientSlug) {
        console.error('Invalid client slug:', clientSlug);
        return;
      }
      // Navigate to the new client page
      this.$router.push(`/client/${clientSlug}`).then(() => {
        // Scroll to top after navigation
        this.onTop();
      });
    },
    onTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  },
  mounted() {
    const route = useRoute();
    this.clientSlug = route.params.slug as string;
    if (this.clientSlug) {
      this.fetchClientData();
    } else {
      console.error('No slug provided in route parameters');
    }
  },
  watch: {
    '$route.params.slug'(newSlug: string) {
      this.clientSlug = newSlug;
      if (newSlug) {
        this.fetchClientData();
      } else {
        console.error('New slug is empty');
      }
    }
  }
});
</script>

<style scoped>
.customer-detail-section .customer-detail-inner .customer-detail-sidebar .related-case-study ul li:empty {
  display: none;
}
.related-case-study ul li a {
  display: block;
  cursor: pointer;
}
.related-case-study ul li a img {
  width: 120px;
  height: 98px;
  object-fit: contain;
}
</style>
